import { Box, Button, Card, Chip, Typography } from '@mui/material'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import WTypography from '../../../../Components/whiteTypography'
import { isEmpty } from 'lodash'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome' // LAP-733 (GE022) round 10
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward' // LAP-CP01
import RestartAltIcon from '@mui/icons-material/RestartAlt' // LAP-CP02
import HistoryIcon from '@mui/icons-material/History' // LAP-CP02
import { useSelector, useDispatch } from 'react-redux'
import LockIcon from '@mui/icons-material/Lock'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import dayjs from 'dayjs' // LAP-CP02
import 'dayjs/locale/fr'
import relativeTime from 'dayjs/plugin/relativeTime'
import { makeGetRequest } from 'utils/api' // LAP-733 (GE022) round 10
import urls from 'utils/urls' // LAP-733 (GE022) round 10
import RecommandationsHistoryModal from './RecommandationsHistoryModal' // LAP-CP02
import { loadWallet } from '../../../../Components/Billing/billingThunks' // LAP-CP04

dayjs.extend(relativeTime)

/**
 * ConseilsCard — Page "Mon bilan patrimonial" (/MonPatrimoine).
 *
 * LAP-CP01 — La card entière est cliquable et amène à /conseiller-patrimonial
 * (le vrai conseiller patrimonial IA : score + diagnostic + actions + projets).
 *
 * LAP-CP02 — Trois actions distinctes sur le conseil du jour :
 *   - "Nouveau rapport" : régénère via l'IA (coût 1 crédit côté backend)
 *   - "Historique" : ouvre un modal listant tous les conseils passés
 *   - Click sur la card : amène à la sous-page Conseiller Patrimonial
 *
 * LAP-CP01 (fallback langue) — garde-fou anti-bug "réponse en allemand sur UI FR".
 */
function ConseilsCard({ oldData, updateReports }) {
  const { t, i18n } = useTranslation('layout') // LAP-733 (GE022)
  const navigate = useNavigate()
  const dispatch = useDispatch() // LAP-CP04
  const siteUser = useSelector((store) => store.user)
  const [regenerating, setRegenerating] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false) // LAP-CP02

  useEffect(() => {}, [oldData])

  // LAP-CP01 — navigation vers la sous-page Conseiller Patrimonial.
  const goToAdvisor = (e) => {
    if (e) e.stopPropagation()
    navigate('/conseiller-patrimonial')
  }

  // LAP-CP02 — régénération du rapport. Le backend consomme 1 crédit par
  // requête (cf. spec pricing : 0,20€ = 1 crédit, marge 80% sur coût API).
  // L'axios interceptor attache déjà Accept-Language et ?language=<lang>
  // donc le backend renvoie un nouveau conseil dans la langue utilisateur.
  //
  // LAP-CP04 — Après l'appel, on rafraîchit le wallet pour que le solde de
  // crédits affiché dans la NavBar se mette à jour. La modale "Crédits
  // insuffisants" est globale (cf. App.js) et écoute déjà l'évènement DOM
  // émis par utils/api.js en cas de HTTP 402, donc rien à câbler ici pour ça.
  const handleNewReport = async (e) => {
    if (e) e.stopPropagation()
    setRegenerating(true)
    try {
      await makeGetRequest(`${urls.MonBilanPatrimonialReports}?refresh=conseil`, {}, true)
      if (typeof updateReports === 'function') {
        await updateReports()
      }
      // LAP-CP04 — refresh solde de crédits (sans bloquer en cas d'erreur,
      // on ne veut pas annuler la régénération si le wallet est down).
      try {
        await dispatch(loadWallet())
      } catch (_) {
        // silencieux
      }
    } finally {
      setRegenerating(false)
    }
  }

  const openHistory = (e) => {
    if (e) e.stopPropagation()
    setHistoryOpen(true)
  }

  // LAP-733 (GE022) — display "Life Agent" instead of any backend AI/OpenAI signature.
  const displaySource = (() => {
    const src = oldData?.source
    if (!src) return src
    if (/(open\s*ai|openai|chat\s*gpt|chatgpt|gpt-?\d)/i.test(String(src))) {
      return t('monPatrimoine.lifeAgentSignature')
    }
    return src
  })()

  // LAP-CP01 — détection langue allemande sur UI FR (garde-fou anti-bug
  // backend qui ne transmet pas user_locale au prompt LLM).
  const isContentInWrongLocale = (content) => {
    if (!content) return false
    const lang = (i18n.language || 'fr').split('-')[0]
    if (lang !== 'fr') return false
    const germanMarkers =
      /\b(Sie|Ihre|Ihren|Ihrer|Ausgaben|Einnahmen|nicht|Konzentrieren|reduzieren|erstellen|um \d+%)\b/i
    return germanMarkers.test(String(content))
  }
  const safeContent =
    !isEmpty(oldData) && isContentInWrongLocale(oldData.content)
      ? t('monPatrimoine.conseilFallback')
      : oldData?.content

  // LAP-CP02 — date du dernier rapport. Le champ exact varie selon les
  // versions backend (created_at, generated_at…) : on tente plusieurs noms.
  const reportDate =
    oldData?.created_at ||
    oldData?.createdAt ||
    oldData?.generated_at ||
    oldData?.date ||
    oldData?.timestamp
  const lang = (i18n.language || 'fr').split('-')[0]
  const formattedDate = (() => {
    if (!reportDate) return ''
    try {
      const dj = dayjs(reportDate).locale(lang === 'fr' ? 'fr' : 'en')
      const absolute = dj.format(
        lang === 'fr' ? 'D MMMM YYYY · HH[h]mm' : 'MMMM D, YYYY · HH:mm',
      )
      const relative = dj.fromNow()
      return { absolute, relative }
    } catch {
      return { absolute: String(reportDate), relative: '' }
    }
  })()

  const isFree = siteUser?.user?.has_subscription_type === 'free'

  return (
    <>
      <Card
        className={`dashboard_card__root1 ${isFree ? '' : 'no-drag'}`}
        onClick={isFree ? undefined : goToAdvisor}
        sx={
          isFree
            ? {}
            : {
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                transition:
                  'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
                border: '1px solid rgba(155,140,255,0.20)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(155,140,255,0.25)',
                  borderColor: 'rgba(155,140,255,0.55)',
                },
                // Halo décoratif en haut à droite — accord violet AI
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: -60,
                  right: -60,
                  width: 180,
                  height: 180,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(155,140,255,0.22) 0%, transparent 70%)',
                  pointerEvents: 'none',
                },
              }
        }
      >
        {isFree ? (
          <Box
            sx={{
              padding: 4,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{ color: '#e0e0e0', fontWeight: 600, marginBottom: 4 }}
            >
              {t('monPatrimoine.advice')}
            </Typography>
            <LockIcon sx={{ fontSize: 80, color: '#5a5a6b', marginBottom: 4 }} />
            <Typography
              variant="h6"
              sx={{ color: '#e0e0e0', marginBottom: 6, lineHeight: 1.5 }}
            >
              {t('monPatrimoine.getPersonalAdvice')}
            </Typography>
            <Button
              variant="contained"
              className="no-drag"
              sx={{
                width: '100%',
                backgroundColor: '#38384d',
                color: '#e0e0e0',
                padding: '12px 0',
                fontSize: '1rem',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#4a4a5f' },
              }}
              onClick={() => navigate('/subscription')}
            >
              {t('monPatrimoine.upgradeToStandard')}
            </Button>
          </Box>
        ) : (
          <>
            {/* LAP-CP02 — Header : icône AI + titre + chip score à droite */}
            <Box
              className="dashboard_card__header1"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesomeIcon sx={{ color: '#9b8cff', fontSize: 22 }} />
                <WTypography component={'span'} variant="h6">
                  {t('monPatrimoine.adviceAndTips')}
                </WTypography>
              </Box>
              {/* LAP-CP01 — Chip teaser score (statique en attendant le backend) */}
              <Chip
                label={t('monPatrimoine.scorePreview', { defaultValue: 'Score 38/100' })}
                size="small"
                sx={{
                  bgcolor: 'rgba(251,191,36,0.18)',
                  color: '#fbbf24',
                  fontWeight: 700,
                  height: 22,
                }}
              />
            </Box>

            {/* LAP-CP02 — Sous-header : "Conseil du jour · Généré il y a X" */}
            <Box
              className="dashboard_card__header1"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <WTypography sx={{ color: '#ffffffa1' }} variant={'body2'}>
                {t('monPatrimoine.tipOfTheDay')}
              </WTypography>
              {formattedDate?.relative && (
                <WTypography sx={{ color: '#ffffffa1' }} variant={'body2'}>
                  ·{' '}
                  {t('monPatrimoine.generatedRelative', {
                    when: formattedDate.relative,
                    defaultValue: 'Généré {{when}}',
                  })}
                </WTypography>
              )}
            </Box>

            {/* LAP-CP02 — Zone conseil : suppression du gros cadre violet,
                remplacé par une bordure gauche subtile + typo plus aérée. */}
            <Box
              mt={4}
              mb={2}
              sx={{ position: 'relative', zIndex: 1, flex: 1, minHeight: 0, px: 1 }}
            >
              {isEmpty(oldData) ? (
                <Box
                  color={'white'}
                  sx={{
                    fontStyle: 'italic',
                    fontWeight: 600,
                    textAlign: 'center',
                    lineHeight: 1.65,
                    px: 1,
                  }}
                >
                  {t('monPatrimoine.defaultTip')}
                </Box>
              ) : (
                <>
                  {/* LAP-CP05 — Signature "Life Agent" placée À L'INTÉRIEUR
                      du conteneur borderLeft pour qu'elle suive le bas du
                      texte. Avant, elle était dans un Box séparé qui flottait
                      à droite à hauteur fixe et chevauchait les boutons. */}
                  <Box
                    sx={{
                      borderLeft: '3px solid #9b8cff',
                      pl: 2.5,
                      pr: 1,
                      py: 1,
                      color: 'white',
                      fontStyle: 'italic',
                      lineHeight: 1.7,
                      fontSize: 16,
                    }}
                  >
                    <Box>
                      {safeContent /* LAP-CP01 — fallback langue allemande appliqué */}
                    </Box>
                    <Box
                      mt={1.5}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.75,
                        color: '#9b8cff',
                        fontSize: 13,
                        fontWeight: 600,
                        letterSpacing: 0.3,
                        fontStyle: 'normal',
                      }}
                    >
                      <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                      {displaySource}
                    </Box>
                  </Box>
                  {oldData?.function && (
                    <Box color={'white'} textAlign={'center'} mt={2}>
                      {oldData?.function}
                      <Box
                        className="no-drag"
                        sx={{ cursor: 'pointer' }}
                        component={'span'}
                        ml={2}
                        onClick={(e) => {
                          e.stopPropagation() // LAP-CP01 — ne pas naviguer
                          navigate(oldData.function_link)
                        }}
                      >
                        <KeyboardArrowRightIcon
                          sx={{ bgcolor: '#438295', borderRadius: '50%' }}
                        />
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>

            {/* LAP-CP02 — Boutons d'action : Nouveau rapport + Historique */}
            <Box
              className="no-drag"
              sx={{
                display: 'flex',
                gap: 1,
                mx: 2,
                mb: 1.5,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Button
                fullWidth
                onClick={handleNewReport}
                disabled={regenerating}
                startIcon={
                  <RestartAltIcon
                    sx={{
                      animation: regenerating ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                }
                sx={{
                  bgcolor: 'rgba(155,140,255,0.18)',
                  color: '#d4ccff',
                  fontWeight: 600,
                  textTransform: 'none',
                  border: '1px solid rgba(155,140,255,0.4)',
                  py: 0.75,
                  '&:hover': {
                    bgcolor: 'rgba(155,140,255,0.30)',
                    borderColor: '#9b8cff',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(212,204,255,0.5)',
                    borderColor: 'rgba(155,140,255,0.2)',
                  },
                }}
              >
                {regenerating
                  ? t('monPatrimoine.regenerating', { defaultValue: 'Génération…' })
                  : t('monPatrimoine.newReport', { defaultValue: 'Nouvelle recommandation' })}
              </Button>
              <Button
                fullWidth
                onClick={openHistory}
                startIcon={<HistoryIcon />}
                sx={{
                  color: '#dbe5f7',
                  fontWeight: 600,
                  textTransform: 'none',
                  border: '1px solid rgba(255,255,255,0.18)',
                  py: 0.75,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.06)',
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                }}
              >
                {t('monPatrimoine.viewHistory', { defaultValue: 'Historique' })}
              </Button>
            </Box>

            {/* LAP-CP01 — CTA principal vers la sous-page Conseiller Patrimonial */}
            <Box
              className="no-drag"
              onClick={goToAdvisor}
              sx={{
                mx: 2,
                mb: 2,
                py: 1,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                bgcolor: 'rgba(59,130,246,0.18)',
                border: '1px solid rgba(59,130,246,0.4)',
                borderRadius: 1.5,
                cursor: 'pointer',
                transition: 'all .2s',
                position: 'relative',
                zIndex: 1,
                '&:hover': {
                  bgcolor: 'rgba(59,130,246,0.30)',
                  borderColor: '#3b82f6',
                },
              }}
            >
              <WTypography sx={{ color: '#a8c1f0', fontWeight: 600 }} variant="body2">
                {t('monPatrimoine.voirAnalyseComplete', {
                  defaultValue: 'Voir mon analyse patrimoniale complète',
                })}
              </WTypography>
              <ArrowOutwardIcon sx={{ color: '#a8c1f0', fontSize: 18 }} />
            </Box>
          </>
        )}
      </Card>

      {/* LAP-CP02 — Modal historique (rendu hors de la card pour éviter
          que onClick parent ne déclenche la navigation) */}
      <RecommandationsHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        currentReport={oldData}
      />
    </>
  )
}

export default ConseilsCard
